/**
 * Hook per controllare lo stato "prezzi disallineati" di un singolo preventivo.
 * Usa la stessa euristica di useDashboardData ma scopata solo all'estimateId.
 *
 * Skip se preventivo locked (won/lost/contracted) — prezzi congelati apposta.
 */
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { isStratigraphyPriceStale, extractMaterialIds } from '@/utils/estimates/priceStaleCheck';

export interface EstimateStaleStatus {
  isStale: boolean;
  staleRowsCount: number;
  totalRowsCount: number;
  /** Status del preventivo: se locked, isStale è sempre false. */
  locked: boolean;
}

const LOCKED_STATUSES = ['won', 'lost', 'contracted'];

export const useEstimateStaleStatus = (estimateId: string | null | undefined): EstimateStaleStatus | null => {
  const { data } = useQuery({
    queryKey: ['estimate-stale-status', estimateId],
    enabled: !!estimateId,
    staleTime: 60_000,
    queryFn: async (): Promise<EstimateStaleStatus> => {
      if (!estimateId) {
        return { isStale: false, staleRowsCount: 0, totalRowsCount: 0, locked: false };
      }

      // 1. Verifica status (skip se locked)
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data: est } = await (supabase as any)
        .from('estimates')
        .select('status')
        .eq('id', estimateId)
        .maybeSingle();
      const locked = !!est && LOCKED_STATUSES.includes(est.status);
      if (locked) {
        return { isStale: false, staleRowsCount: 0, totalRowsCount: 0, locked: true };
      }

      // 2. Carica stratigrafie del preventivo
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data: stratis } = await (supabase as any)
        .from('estimate_stratigraphies')
        .select('id, prices_updated_at, stratigraphy_data, layers_data, original_stratigraphy_id')
        .eq('estimate_id', estimateId);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const stratList = (stratis as any[] | null) ?? [];
      if (stratList.length === 0) {
        return { isStale: false, staleRowsCount: 0, totalRowsCount: 0, locked: false };
      }

      // 3. Le PARETI collegate sono state modificate dopo l'ultimo ricalcolo?
      // Va controllato prima dei materiali: senza, una parete stravolta nel
      // configuratore non accendeva nulla, perche' quel salvataggio tocca
      // `stratigraphies` e `layers` ma mai `materials`.
      const stratIds = Array.from(new Set(
        stratList.map(s => s.original_stratigraphy_id).filter(Boolean) as string[],
      ));
      const stratUpdatedAt = new Map<string, number>();
      if (stratIds.length > 0) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const { data: strats } = await (supabase as any)
          .from('stratigraphies')
          .select('id, updated_at')
          .in('id', stratIds);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        ((strats as any[] | null) ?? []).forEach(st => {
          if (st?.id && st?.updated_at) stratUpdatedAt.set(st.id, new Date(st.updated_at).getTime());
        });
      }
      const parteCambiata = (s: { original_stratigraphy_id?: string | null; prices_updated_at?: string | null }) => {
        if (!s.original_stratigraphy_id) return false;
        const u = stratUpdatedAt.get(s.original_stratigraphy_id);
        if (u === undefined) return false;
        return u > (s.prices_updated_at ? new Date(s.prices_updated_at).getTime() : 0);
      };

      // 4. Raccogli id materials referenziati e carica updated_at solo per quelli
      const allMatIds = new Set<string>();
      for (const s of stratList) extractMaterialIds(s).forEach(id => allMatIds.add(id));
      if (allMatIds.size === 0) {
        const soloPareti = stratList.filter(parteCambiata).length;
        return {
          isStale: soloPareti > 0,
          staleRowsCount: soloPareti,
          totalRowsCount: stratList.length,
          locked: false,
        };
      }

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data: mats } = await (supabase as any)
        .from('materials')
        .select('id, updated_at')
        .in('id', Array.from(allMatIds));

      const matMap = new Map<string, number>();
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      ((mats as any[] | null) ?? []).forEach(m => {
        if (m?.id && m?.updated_at) matMap.set(m.id, new Date(m.updated_at).getTime());
      });

      // 5. Conta righe stale: parete modificata OPPURE materiali aggiornati
      let staleRowsCount = 0;
      for (const s of stratList) {
        if (parteCambiata(s) || isStratigraphyPriceStale(s, matMap)) staleRowsCount++;
      }
      return {
        isStale: staleRowsCount > 0,
        staleRowsCount,
        totalRowsCount: stratList.length,
        locked: false,
      };
    },
  });

  return useMemo(() => data ?? null, [data]);
};
